import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import UserDropdown from './UserDropdown';
import { cn } from '@/lib/utils';

export interface BreadcrumbEntry {
  label: string;
  /** Absent pour la page courante. */
  to?: string;
}

interface PageHeaderProps {
  title: React.ReactNode;
  /** Une ligne au plus : le contenu utile doit commencer haut dans la page. */
  description?: React.ReactNode;
  /** Fil d'Ariane, affiché sur les pages détaillées. */
  breadcrumbs?: BreadcrumbEntry[];
  /** Actions de la page (bouton principal, menu secondaire…). */
  children?: React.ReactNode;
  className?: string;
}

/**
 * En-tête compact commun à toutes les pages : titre de 24 px, une ligne de
 * contexte, actions alignées à droite. Remplace les grands bandeaux à
 * dégradé qui repoussaient les données sous la ligne de flottaison.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({ title, description, breadcrumbs, children, className }) => {
  return (
    <header className={cn("mb-4 flex flex-col gap-1", className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Fil d'Ariane">
          <ol className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
            {breadcrumbs.map((crumb, index) => (
              <li key={`${crumb.label}-${index}`} className="flex items-center gap-1">
                {index > 0 && <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
                {crumb.to ? (
                  <Link to={crumb.to} className="rounded-sm hover:text-foreground hover:underline underline-offset-4">
                    {crumb.label}
                  </Link>
                ) : (
                  <span aria-current="page" className="text-foreground">{crumb.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground truncate">{title}</h1>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {children}
          <UserDropdown />
        </div>
      </div>
    </header>
  );
};
